import { useTranslation } from "react-i18next";
import { ILLUSTRATION_LEVELS } from "../lib/s1000dData";
import { useEstimate } from "../store/EstimateContext";
import { fmtHours, num } from "../lib/calculations";
import MatrixImporter from "./MatrixImporter";

import HelpIconButton from "./HelpIconButton";
export default function IllustrationsTab() {
  const { t } = useTranslation();
  const { estimate, totals, updateIllusCell } = useEstimate();

  return (
    <section className="space-y-4" data-testid="illustrations-tab">
      <div className="flex items-start justify-between gap-6">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            {t("illustrations.section")}
          </p>
          <h2 className="font-heading text-2xl tracking-tight font-bold text-black flex items-center gap-2">
            {t("illustrations.title")}
            <HelpIconButton anchor="ch2" testId="help-icon-illustrations" />
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            {t("illustrations.description")}
          </p>
        </div>

        <div className="border border-gray-300 px-4 py-3 bg-white min-w-[180px]" data-testid="illus-hours-badge">
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            {t("illustrations.headline")}
          </p>
          <p className="font-mono text-2xl font-bold text-black leading-none mt-1">
            {fmtHours(totals.illusHours)}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">
            {totals.totalIllus} {t("units.illusTotal")}
          </p>
        </div>
      </div>

      <MatrixImporter target="illus" testidPrefix="illus" />

      <div className="bg-white border border-gray-300 overflow-x-auto">
        <table className="w-full text-sm text-left border-collapse" data-testid="illustrations-table">
          <thead>
            <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px] border-y border-gray-300">
              <th className="py-3 px-3">{t("tableHeaders.complexity")}</th>
              <th className="py-3 px-3">{t("tableHeaders.description")}</th>
              <th className="py-3 px-3 text-right">{t("illustrations.quantity")}</th>
              <th className="py-3 px-3 text-right">{t("illustrations.hoursPerUnit")}</th>
              <th className="py-3 px-3 text-right">{t("tableHeaders.totalHours")}</th>
            </tr>
          </thead>
          <tbody>
            {ILLUSTRATION_LEVELS.map((lvl) => {
              const cell = estimate.illustrations[lvl.key] || { qty: 0, hours: lvl.defaultHours };
              const total = num(cell.qty) * num(cell.hours);
              return (
                <tr key={lvl.key} className="border-b border-gray-200 hover:bg-[#F9FAFB]">
                  <td className="py-3 px-3">
                    <div className="font-bold text-black">{t(`levels.${lvl.key}`)}</div>
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">
                      {t("levels.defaultHrs", { n: lvl.defaultHours })}
                    </div>
                  </td>
                  <td className="py-3 px-3 text-gray-600">{t(`illusLevels.${lvl.key}Subtitle`)}</td>
                  <td className="py-2 px-3 text-right">
                    <input
                      type="number"
                      min="0"
                      value={cell.qty}
                      onChange={(e) => updateIllusCell(lvl.key, "qty", e.target.value)}
                      data-testid={`illus-${lvl.key}-qty`}
                      className="border border-gray-300 rounded-none px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] font-mono text-sm w-24 text-right"
                    />
                  </td>
                  <td className="py-2 px-3 text-right">
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={cell.hours}
                      onChange={(e) => updateIllusCell(lvl.key, "hours", e.target.value)}
                      data-testid={`illus-${lvl.key}-hours`}
                      className="border border-gray-300 rounded-none px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] font-mono text-sm w-24 text-right"
                    />
                  </td>
                  <td
                    className="py-3 px-3 font-mono text-right text-black"
                    data-testid={`illus-${lvl.key}-total`}
                  >
                    {fmtHours(total)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-black text-white font-bold uppercase tracking-wider text-[10px]">
              <td className="py-3 px-3" colSpan={4}>{t("tableHeaders.projectTotal")}</td>
              <td className="py-3 px-3 font-mono text-right text-sm normal-case" data-testid="illus-grand-total">
                {fmtHours(totals.illusHours)} hrs
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}
